const copy = {
  en: {
    connection: { retrying: 'Reconnecting', initial: 'Connecting', succeeded: 'Connected', exhausted: 'Connection lost', blocked: 'Connection unavailable' },
    request: { retrying: 'Retrying', initial: 'Sending', succeeded: 'Retry succeeded', exhausted: 'Request failed', blocked: 'Request blocked' },
    cancelled: 'Stopped',
    reason: { auth_required: 'Sign in again to continue', forbidden: 'Access denied' },
    delay: 'delay {s} s', next: 'next attempt in {s} s', due: 'next attempt now',
  },
  cs: {
    connection: { retrying: 'Obnova spojení', initial: 'Připojování', succeeded: 'Připojeno', exhausted: 'Spojení ztraceno', blocked: 'Spojení není dostupné' },
    request: { retrying: 'Opakování', initial: 'Odesílání', succeeded: 'Opakování uspělo', exhausted: 'Požadavek selhal', blocked: 'Požadavek je zablokován' },
    cancelled: 'Zastaveno',
    reason: { auth_required: 'Je nutné se znovu přihlásit', forbidden: 'Přístup byl odepřen' },
    delay: 'prodleva {s} s', next: 'další pokus za {s} s', due: 'další pokus hned',
  },
  sk: {
    connection: { retrying: 'Obnova spojenia', initial: 'Pripájanie', succeeded: 'Pripojené', exhausted: 'Spojenie stratené', blocked: 'Spojenie nie je dostupné' },
    request: { retrying: 'Opakovanie', initial: 'Odosielanie', succeeded: 'Opakovanie uspelo', exhausted: 'Požiadavka zlyhala', blocked: 'Požiadavka je zablokovaná' },
    cancelled: 'Zastavené',
    reason: { auth_required: 'Je potrebné znova sa prihlásiť', forbidden: 'Prístup bol odmietnutý' },
    delay: 'prestávka {s} s', next: 'ďalší pokus o {s} s', due: 'ďalší pokus hneď',
  },
};

const seconds = (text, value, locale) =>
  text.replace('{s}', new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(value));

/**
 * Whether a model retry still deserves its persistent status line. The daemon keeps a settled episode until
 * the turn ends, so a succeeded or cancelled one would otherwise stay on screen while the agent works on.
 * @param {import('./retry.mjs').RetryProgress} progress
 * @returns {boolean}
 */
export function modelRetryShown(progress) {
  return progress.phase !== 'succeeded' && progress.phase !== 'cancelled';
}

/**
 * One short status for a retry transition, the same on every surface, e.g. "Reconnecting 2/5 · delay 4 s".
 * Pure: the caller supplies its resolved locale and owns state, announcements and any countdown.
 * @param {import('./retry.mjs').RetryProgress} progress
 * @param {'cs' | 'sk' | 'en'} locale
 * @param {{ delay?: boolean }} [options] `delay: false` drops the announced delay when the caller shows a live
 *   countdown ({@link formatRetryCountdown}) in its place.
 * @returns {string}
 */
export function formatRetryProgress(progress, locale, options = {}) {
  const text = copy[locale];
  const lane = progress.mode === 'request' ? text.request : text.connection;
  const { phase, retryNumber, maxRetries, reason } = progress;
  if (phase === 'cancelled') return text.cancelled;
  if (phase === 'succeeded') return lane.succeeded;
  if (phase === 'exhausted' || phase === 'blocked') {
    // Signing in or access is the one thing the reader must change, so it replaces the generic outcome.
    return reason === 'auth_required' || reason === 'forbidden' ? text.reason[reason] : lane[phase];
  }
  // The initial attempt is not a retry: it shows no number, and an unknown number stays unstated.
  if (retryNumber === 0) return lane.initial;
  const number = new Intl.NumberFormat(locale);
  const count = retryNumber === null ? ''
    : maxRetries === null ? ` ${number.format(retryNumber)}` : ` ${number.format(retryNumber)}/${number.format(maxRetries)}`;
  const parts = [lane.retrying + count];
  // Native delay is the announced duration, not a countdown calculated from wall time.
  if (phase === 'scheduled' && options.delay !== false && progress.delayMs !== null) {
    parts.push(seconds(text.delay, progress.delayMs / 1000, locale));
  }
  return parts.join(' · ');
}

/**
 * The visual countdown to a locally owned scheduled attempt. The caller reads its own clock.
 * @param {number} remainingMs
 * @param {'cs' | 'sk' | 'en'} locale
 * @returns {string}
 */
export function formatRetryCountdown(remainingMs, locale) {
  const text = copy[locale];
  const remaining = Math.ceil(remainingMs / 1000);
  return remaining > 0 ? seconds(text.next, remaining, locale) : text.due;
}
